import { Icon } from '@iconify/react'
import { Card } from '@/components/ui/Card'
import { IconButton } from '@/components/ui/Button'
import { Skeleton } from '@/components/ui/Skeleton'
import { OcrStatusBadge } from '@/components/shared/StatusBadge'
import { DateDisplay } from '@/components/shared/DateDisplay'
import { useTenderAttachments, getAttachmentUrl } from '@/hooks/useAttachments'
import { cn } from '@/lib/utils'
import { ATTACHMENT_ROLE_LABELS } from '@/lib/constants'
import type { Attachment, AttachmentRole } from '@/lib/pocketbase'

interface DocumentsTabProps {
  tenderId: string
}

export function TenderDocumentsTab({ tenderId }: DocumentsTabProps) {
  const { data: attachments, isLoading } = useTenderAttachments(tenderId)

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-4 p-4 border border-slate-200 rounded-lg">
              <Skeleton variant="rectangular" className="w-10 h-10" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>
          ))}
        </div>
      </Card>
    )
  }

  if (!attachments || attachments.length === 0) {
    return (
      <Card className="p-8 text-center">
        <Icon
          icon="solar:folder-with-files-line-duotone"
          className="w-12 h-12 text-slate-300 mx-auto mb-3"
        />
        <h3 className="font-semibold text-slate-900">ไม่มีเอกสาร</h3>
        <p className="text-sm text-slate-500 mt-1">
          เอกสารและไฟล์แนบของประกาศนี้จะแสดงที่นี่
        </p>
      </Card>
    )
  }

  // Group by role
  const groupedAttachments = attachments.reduce((acc, att) => {
    const role = att.role || 'other'
    if (!acc[role]) acc[role] = []
    acc[role].push(att)
    return acc
  }, {} as Record<string, Attachment[]>)

  return (
    <div className="space-y-6">
      {/* Document stats */}
      <div className="flex items-center gap-6 text-sm text-slate-600">
        <span className="flex items-center gap-2">
          <Icon icon="solar:folder-line-duotone" className="w-4 h-4 text-slate-400" />
          {attachments.length} เอกสาร
        </span>
      </div>

      {/* Documents by group */}
      {Object.entries(groupedAttachments).map(([role, docs]) => (
        <Card key={role} padding="none">
          {role !== 'other' && (
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
              <h3 className="font-medium text-slate-900">
                {ATTACHMENT_ROLE_LABELS[role as AttachmentRole] || role}
              </h3>
              <p className="text-sm text-slate-500">
                {docs.length} เอกสาร
              </p>
            </div>
          )}
          <div className="divide-y divide-slate-100">
            {docs.map((doc) => (
              <DocumentItem key={doc.id} document={doc} showOcr />
            ))}
          </div>
        </Card>
      ))}

    </div>
  )
}

function DocumentItem({
  document,
  showOcr,
}: {
  document: Attachment
  showOcr: boolean
}) {
  const fileUrl = getAttachmentUrl(document)
  const fileExtension = document.file?.split('.').pop()?.toLowerCase() || ''

  const getFileIcon = (ext: string) => {
    const icons: Record<string, string> = {
      pdf: 'solar:document-text-line-duotone',
      doc: 'solar:document-line-duotone',
      docx: 'solar:document-line-duotone',
      xls: 'solar:chart-2-line-duotone',
      xlsx: 'solar:chart-2-line-duotone',
      jpg: 'solar:gallery-line-duotone',
      jpeg: 'solar:gallery-line-duotone',
      png: 'solar:gallery-line-duotone',
      zip: 'solar:zip-file-line-duotone',
      rar: 'solar:zip-file-line-duotone',
    }
    return icons[ext] || 'solar:file-line-duotone'
  }

  const getFileColor = (ext: string) => {
    const colors: Record<string, string> = {
      pdf: 'text-rose-600 bg-rose-100',
      doc: 'text-blue-600 bg-blue-100',
      docx: 'text-blue-600 bg-blue-100',
      xls: 'text-emerald-600 bg-emerald-100',
      xlsx: 'text-emerald-600 bg-emerald-100',
      jpg: 'text-violet-600 bg-violet-100',
      jpeg: 'text-violet-600 bg-violet-100',
      png: 'text-violet-600 bg-violet-100',
    }
    return colors[ext] || 'text-slate-600 bg-slate-100'
  }

  return (
    <div className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors">
      {/* File icon */}
      <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center', getFileColor(fileExtension))}>
        <Icon icon={getFileIcon(fileExtension)} className="w-5 h-5" />
      </div>

      {/* File info */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900 truncate">
          {document.file || 'เอกสารไม่มีชื่อ'}
        </p>
        <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
          <span className="uppercase">{fileExtension}</span>
          {document.published_at && (
            <DateDisplay date={document.published_at} format="relative" />
          )}
        </div>
      </div>

      {/* OCR status */}
      {showOcr && document.ocr_status && (
        <OcrStatusBadge status={document.ocr_status as any} />
      )}

      {/* Actions */}
      <div className="flex items-center gap-2">
        {showOcr && document.ocr_status === 'done' && (
          <IconButton
            icon="solar:document-text-line-duotone"
            label="ดูข้อความ OCR"
            variant="ghost"
            size="sm"
          />
        )}
        <a href={fileUrl} target="_blank" rel="noopener noreferrer">
          <IconButton
            icon="solar:download-minimalistic-line-duotone"
            label="ดาวน์โหลด"
            variant="ghost"
            size="sm"
          />
        </a>
      </div>
    </div>
  )
}
